import { useMutation } from "@tanstack/react-query";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { dateValueToString } from "@/components/shared/formElements/DatePicker";
import { createOpenApiConfiguration, getApiErrorMessage } from "@/lib/utils";
import {
  EXISTING_STUDIO_SIGNIN_MESSAGE,
  isExistingStudioSignup,
} from "@/lib/utils/auth/signup-email-status";
import { utcNoonFromYmd } from "@/lib/utils/date/date-only";
import { type Gender, UsersApi } from "@/openapi/generated";
import type { ResultOfCreateUserResponse } from "@/openapi/generated/models";
import type { SignUpFormSchema } from "@/schemas";

function getFailedRegistrationMessage(
  result: ResultOfCreateUserResponse,
): string {
  const message = result.error?.message?.trim();
  return message || "Failed to create account";
}

export interface UseAuthSignUpOptions {
  /**
   * Callback executed after successful registration (before auto-login).
   * Use to record waiver/policy acceptances via POST /api/policies/{id}/accept.
   */
  onRegisterSuccess?: (variables: SignUpFormSchema) => void | Promise<void>;
  /**
   * Callback executed on successful sign up and auto-login
   */
  onSuccess?: () => void;
  /**
   * Callback executed on error
   */
  onError?: (error: string) => void;
}

export interface UseAuthSignUpReturn {
  /**
   * Sign up function
   */
  mutate: (data: SignUpFormSchema) => void;
  /**
   * Loading state
   */
  isPending: boolean;
  /**
   * Error state
   */
  isError: boolean;
  /**
   * Success state (after signup, before auto-login)
   */
  isSuccess: boolean;
  /**
   * Auto-login success state
   */
  isAutoLoginSuccess: boolean;
  /**
   * Auto-login error message
   */
  autoLoginError: string | null;
  /**
   * True when we added Member to an existing studio account and skipped auto-login.
   */
  didSkipAutoLogin: boolean;
}

export const useAuthSignUp = (
  options?: UseAuthSignUpOptions,
): UseAuthSignUpReturn => {
  const service = new UsersApi(createOpenApiConfiguration());
  const [isAutoLoginSuccess, setIsAutoLoginSuccess] = useState(false);
  const [autoLoginError, setAutoLoginError] = useState<string | null>(null);
  const [didSkipAutoLogin, setDidSkipAutoLogin] = useState(false);

  const { mutate, isPending, isError, isSuccess } = useMutation({
    retry: false,
    mutationFn: async (data: SignUpFormSchema) => {
      const locationIds = data.locations.map((id) => Number(id));
      const existingStudio = isExistingStudioSignup(data.accountKind);

      const result = await service.apiUserRegisterMemberPost({
        createUserRequest: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          cellPhone: data.cellPhone,
          homePhone: null,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          gender: null,
          dateOfBirth: undefined,
          leadSource: data.heardAboutUs ?? "/",
          familyContacts: data.familyContacts.map((contact) => ({
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            cellPhone: contact.cellPhone,
            addressLine1: contact.addressLine1,
          })),
          students:
            data?.students?.map((s) => {
              const ymd = dateValueToString(s.birthday);
              return {
                firstName: s.firstName ?? "",
                lastName: s.lastName ?? "",
                dateOfBirth: utcNoonFromYmd(ymd),
                gender: s.gender as Gender,
                selfEnrollment: data.role === "selfenroll" ? true : false,
                locationIds,
              };
            }) ?? [],
          ...(existingStudio || !data.password
            ? {}
            : { password: data.password }),
        },
      });

      if (!result.isSuccess) {
        throw new Error(getFailedRegistrationMessage(result));
      }

      return result;
    },
    onSuccess: async (data, variables) => {
      try {
        await options?.onRegisterSuccess?.(variables);
      } catch {
        // Don't block auto-login if recording acceptances fails
      }

      if (isExistingStudioSignup(variables.accountKind)) {
        setDidSkipAutoLogin(true);
        setIsAutoLoginSuccess(false);
        setAutoLoginError(EXISTING_STUDIO_SIGNIN_MESSAGE);
        return;
      }

      setDidSkipAutoLogin(false);

      try {
        const result = await signIn("credentials", {
          redirect: false,
          email: variables.email,
          password: variables.password,
        });

        if (result?.error) {
          setAutoLoginError(result.error);
          setIsAutoLoginSuccess(false);
          options?.onError?.(result.error);
        } else {
          setIsAutoLoginSuccess(true);
          setAutoLoginError(null);
          options?.onSuccess?.();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to automatically log in.";
        setAutoLoginError(errorMessage);
        setIsAutoLoginSuccess(false);
        options?.onError?.(errorMessage);
      }
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to create account",
      );
      setAutoLoginError(null);
      setIsAutoLoginSuccess(false);
      setDidSkipAutoLogin(false);
      options?.onError?.(errorMessage);
    },
  });

  return {
    mutate,
    isPending,
    isError,
    isSuccess,
    isAutoLoginSuccess,
    autoLoginError,
    didSkipAutoLogin,
  };
};
